import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  private propertiesPromise = null;
  private publicPropertiesPromise = null;
  private publicPropertyPromiseMap = {};
  private attributesPromise = null;
  private propertiesConversationsPromiseMap = {};
  private propertiesActiveLeasePromiseMap = {};
  private propertiesInvoiceSummaryPromiseMap = {};
  private propertiesInvoicesPromiseMap = {};
  public loading = false;

  constructor(private client: HttpClient) { }

  private inferLeaseType(lease: any) {
    if (!lease) {
      return;
    }
    if (!lease.startDate && !lease.endDate) {
      lease.type = 'M2M';
    } else {
      lease.type = 'TERM';
    }
  }

  getPublicProperties() {
    if(this.publicPropertiesPromise == null) {
      this.loading = true;  
      this.publicPropertiesPromise = this.client.get(`${environment.apiBaseUrl}/v1/public/properties`).toPromise();

    }

    return this.publicPropertiesPromise;
  }

  /**
   * List properties for the current organization. Archived properties are
   * excluded by default so operational surfaces (dashboard, pickers) stay
   * clean. Pass `true` for the Archived Properties page, Reports/tax exports,
   * or any admin surface that needs to see everything.
   */
  getProperties(includeArchived: boolean = false) {
    // Bypass the cache when caller wants archived rows so we don't return
    // a filtered promise from an earlier call.
    if (includeArchived) {
      const params = new HttpParams().set('includeArchived', 'true');
      return this.client
        .get(`${environment.apiBaseUrl}/v1/properties`, { params })
        .toPromise();
    }
    if (this.propertiesPromise == null) {
      this.propertiesPromise = this.client.get(`${environment.apiBaseUrl}/v1/properties`).toPromise();
    }
    return this.propertiesPromise;
  }

  /** Return only archived properties (Archived Properties page). */
  getArchivedProperties() {
    const params = new HttpParams().set('archivedOnly', 'true');
    return this.client
      .get(`${environment.apiBaseUrl}/v1/properties`, { params })
      .toPromise();
  }

  getPublicProperty(propertyId) {
    if(!this.publicPropertyPromiseMap[propertyId]) {
      this.publicPropertyPromiseMap[propertyId] = this.client
        .get(`${environment.apiBaseUrl}/v1/public/properties/${propertyId}`)
        .toPromise();
    }

    return this.publicPropertyPromiseMap[propertyId];
  }

  clearProperties() {
    this.propertiesPromise = null;
    this.publicPropertiesPromise = null;
    this.publicPropertyPromiseMap = {};
  }

  getAttributes() {
    if(this.attributesPromise == null) {
      this.attributesPromise = this.client.get(`${environment.apiBaseUrl}/v1/public/attributes`).toPromise();
    }

    return this.attributesPromise;
  }

  // Org-scoped attribute definitions for admin screens (not cached so edits show immediately)
  getAdminAttributes(): Promise<any[]> {
    return this.client.get<any[]>(`${environment.apiBaseUrl}/v1/admin/attributes`).toPromise();
  }

  createAttribute(attribute: { name: string; attributeType: string }) {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/attributes`, attribute, httpOptions).toPromise().then((res) => {
      this.attributesPromise = null;
      return res;
    });
  }

  updateAttribute(id: string, attribute: { name?: string; attributeType?: string }) {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/attributes/${id}`, attribute, httpOptions).toPromise().then((res) => {
      this.attributesPromise = null;
      return res;
    });
  }

  deleteAttribute(id: string) {
    return this.client.delete(`${environment.apiBaseUrl}/v1/admin/attributes/${id}`).toPromise().then((res) => {
      this.attributesPromise = null;
      return res;
    });
  }

  updateProperty(property) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}`, property).toPromise().then((res) => {
      this.clearProperties();
    })
  }

  createProperty(property) {
    // Remove base64 image from property if it exists (too large for payload)
    const propertyToCreate = { ...property };
    if (propertyToCreate.coverImage && propertyToCreate.coverImage.startsWith('data:')) {
      propertyToCreate.coverImage = '';
    }
    
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties`, propertyToCreate).toPromise().then((res) => {
      this.clearProperties();
      return res; // Return the response so we can get the created property ID
    })
  }

  deleteProperty(property) {
	  return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}`).toPromise().then((res) => {
		  this.clearProperties();
    })
  }

  /**
   * Soft-archive a property. History (statements, expenses, invoices, past
   * leases) stays intact so Reports and tax exports keep working.
   */
  archiveProperty(property) {
    return this.client
      .post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/archive`, {})
      .toPromise()
      .then((res) => {
        this.clearProperties();
        return res;
      });
  }

  /** Restore a previously archived property. */
  unarchiveProperty(property) {
    return this.client
      .post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/unarchive`, {})
      .toPromise()
      .then((res) => {
        this.clearProperties();
        return res;
      });
  }

  createInvoice(invoice, property) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices`, invoice).toPromise().then((res) =>{
      this.clearInvoices(property);
    });
  }

  deleteInvoice(property, invoice) {
    return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/${invoice.id}`).toPromise().then((res) => {
      this.clearInvoices(property);
    });
  }

  getConversations(property?, readStatus?, limit?) {
    let cacheKey = (property ? property.id : 'all') + readStatus + limit;
    if(!this.propertiesConversationsPromiseMap[cacheKey]) {
      let params = new HttpParams();

      if(readStatus) {
        params = params.append("readStatus", readStatus);
      }

      if(limit) {
        params = params.append("limit", limit);
      }

      if (property && property.id) {
        this.propertiesConversationsPromiseMap[cacheKey] = this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/conversations`, {params: params}).toPromise();
      } else {
        // Get all conversations for organization
        this.propertiesConversationsPromiseMap[cacheKey] = this.client.get(`${environment.apiBaseUrl}/v1/admin/conversations`, {params: params}).toPromise();
      }
    }

    return this.propertiesConversationsPromiseMap[cacheKey];
  }

  clearConversations(property) {
    for(let cacheKey in this.propertiesConversationsPromiseMap) {
      if(cacheKey.indexOf(property.id) > -1) {
        this.propertiesConversationsPromiseMap[cacheKey] = null;
      } 
    }
  }

  respondToConversation(property, conversation, message, medium) {
    let response = {
      message: message
    };

    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/conversations/${conversation.id}/response?medium=${medium}`, response).toPromise().then((res) => {
      this.clearConversations(property);
    });
  }

  markConversationAsRead(property, conversation) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/conversations/${conversation.id}/read`, {}).toPromise().then((res) => {
      this.clearConversations(property);
    })
  }

  clearLeases(property) {
    this.propertiesActiveLeasePromiseMap[property.id] = null;
    this.clearProperties();
  }

  startLease(property, lease) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/leasee`, lease).toPromise().then((res) => {
      if(this.propertiesActiveLeasePromiseMap[property.id]) {
        this.clearLeases(property);
      }
    });
  }

  /**
   * Upload a lease PDF/image; AI returns draft fields + persisted documentId.
   */
  importLeaseDocument(propertyId, file) {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.client.post(
      `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/leases/import`,
      form
    ).toPromise();
  }

  getLeaseDocuments(propertyId, leaseId) {
    return this.client.get(
      `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/leases/${leaseId}/documents`
    ).toPromise();
  }

  uploadLeaseDocument(propertyId, leaseId, file) {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.client.post(
      `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/leases/${leaseId}/documents`,
      form
    ).toPromise();
  }

  reanalyzeLeaseDocument(propertyId, documentId) {
    return this.client.post(
      `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/documents/${documentId}/analyze`,
      {}
    ).toPromise();
  }

  getLeaseDocumentDownloadUrl(propertyId, documentId) {
    return `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/documents/${documentId}/file`;
  }

  getLeaseDocumentBlob(propertyId, documentId) {
    return this.client.get(
      this.getLeaseDocumentDownloadUrl(propertyId, documentId),
      { responseType: 'blob' }
    ).toPromise();
  }

  downloadLeaseDocument(propertyId, documentId, fileName) {
    return this.getLeaseDocumentBlob(propertyId, documentId).then((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'lease.pdf';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    });
  }

  /**
   * Generate a lease summary PDF from structured lease fields and attach it
   * to the lease (used when no uploaded agreement exists).
   */
  generateLeaseDocument(propertyId, leaseId) {
    return this.client.post(
      `${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/leases/${leaseId}/pdf`,
      {}
    ).toPromise();
  }

  getActiveLease(property) {
    if(!this.propertiesActiveLeasePromiseMap[property.id]) {
      this.propertiesActiveLeasePromiseMap[property.id] = this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/leasee`).toPromise().then((leases: Array<any>) => {
        if(leases && leases.length > 0) {
          for(let i = 0; i < leases.length; i++) {
            if(leases[i].status == "ACTIVE") {
              this.inferLeaseType(leases[i]);
              return leases[i];
            }
          }
        }

        return null;
      }).catch((error) => {
        // Silently fail if endpoint doesn't exist or other error
        console.warn('Error fetching active lease:', error);
        return null;
      });
    }

    return this.propertiesActiveLeasePromiseMap[property.id];
  }

  modifyLease(property, lease) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/leasee/${lease.id}`, lease).toPromise().then((res) => {
      this.clearLeases(property);
      return res;
    });
  }

  getLeaseById(leaseId: string) {
    return this.client.get(`${environment.apiBaseUrl}/v1/admin/leases/${leaseId}`).toPromise().then((lease: any) => {
      this.inferLeaseType(lease);
      return lease;
    });
  }

  getLeases(property, status?) {
    let params = new HttpParams();
    if(status) {
      params = params.append('status', status);
    }

    return this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/leasee`, {params: params}).toPromise().then((leases: any[]) => {
      if(Array.isArray(leases)) {
        leases.forEach(l => this.inferLeaseType(l));
      }
      return leases;
    });
  }

  endLease(property, lease) {
    return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/leasee/${lease.id}`).toPromise().then((res) => {
      this.clearLeases(property);
    });
  }

  getInvoiceSummary(property) {
    if(!this.propertiesInvoiceSummaryPromiseMap[property.id]) {
      this.propertiesInvoiceSummaryPromiseMap[property.id] = this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/summary`).toPromise().catch((error) => {
        // Clear the promise on error so it can be retried
        this.propertiesInvoiceSummaryPromiseMap[property.id] = null;
        throw error;
      });
    }

    return this.propertiesInvoiceSummaryPromiseMap[property.id];
  }

  getInvoices(property, page?: number, pageSize?: number) {
    let params = new HttpParams();
    if (page !== undefined && page !== null) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    
    // Clear cache when pagination is used
    if (page !== undefined || pageSize !== undefined) {
      this.propertiesInvoicesPromiseMap[property.id] = null;
    }
    
    if(!this.propertiesInvoicesPromiseMap[property.id]) {
      this.propertiesInvoicesPromiseMap[property.id] = this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices`, { params }).toPromise();
    }

    return this.propertiesInvoicesPromiseMap[property.id];
  }

  getAllInvoices(page?: number, pageSize?: number, propertyId?: string, leaseeId?: string, startDate?: string, endDate?: string) {
    let params = new HttpParams();
    if (page !== undefined && page !== null) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (propertyId) {
      params = params.set('propertyId', propertyId);
    }
    if (leaseeId) {
      params = params.set('leaseeId', leaseeId);
    }
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    return this.client.get(`${environment.apiBaseUrl}/v1/admin/invoices`, { params }).toPromise();
  }

  getAllLeases(page?: number, pageSize?: number, propertyId?: string, userId?: string, startDate?: string, endDate?: string) {
    let params = new HttpParams();
    if (page !== undefined && page !== null) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (propertyId) {
      params = params.set('propertyId', propertyId);
    }
    if (userId) {
      params = params.set('userId', userId);
    }
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    return this.client.get(`${environment.apiBaseUrl}/v1/admin/leases`, { params }).toPromise().then((response: any) => {
      if (response && response.data && Array.isArray(response.data)) {
        response.data.forEach(l => this.inferLeaseType(l));
      } else if (Array.isArray(response)) {
        response.forEach(l => this.inferLeaseType(l));
      }
      return response;
    });
  }

  clearInvoices(property) {
    this.propertiesInvoiceSummaryPromiseMap[property.id] = null;
    this.propertiesInvoicesPromiseMap[property.id] = null;
  }

  getInvoicePdf(property, invoiceId) {
    return this.client.get(
      `${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/${invoiceId}/pdf`,
      // Unique URL: older API builds sent "Cache-Control: max-age=1 year", so a
      // browser could keep showing an invoice from before a payment was added.
      { responseType: 'blob', params: new HttpParams().set('_', String(Date.now())) }
    ).toPromise();
  }

  generateInvoicePdf(property, invoiceId) {
    return this.client.post(
      `${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/${invoiceId}/pdf`,
      {}
    ).toPromise().then((res: any) => {
      this.clearInvoices(property);
      return res;
    });
  }

  deleteInvoicePdf(property, invoiceId) {
    return this.client.delete(
      `${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/${invoiceId}/pdf`
    ).toPromise().then((res: any) => {
      this.clearInvoices(property);
      return res;
    });
  }

  createPayment(property, payment) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/externalPayment`, payment).toPromise().then((res) => {
      this.clearInvoices(property);
      return res;
    })
  }

  deletePayment(property, invoice, payment) {
    return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${property.id}/invoices/${invoice.id}/payments/${payment.id}`).toPromise().then((res) => {
      this.clearInvoices(property);
      return res;
    });
  }

  getReceiptImage(path: string) {
    return this.client
      .get(`${environment.apiBaseUrl}${path}`, { responseType: 'blob' })
      .toPromise();
  }

  uploadCoverImage(propertyId, image){
      const uploadData = new FormData();
      console.log(image);
      uploadData.append('myFile', image, image.name);
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/coverImage/upload`, uploadData).toPromise().then((res) => {
          this.clearProperties();
          return res;
      });
  }

  uploadExpense(propertyId, image, category, amount, useAi){
      const timestampedName = Date.now() + '-' + image.name;
      const uploadData = new FormData();
      uploadData.append('file', image, timestampedName);
      uploadData.append('category', category);
      uploadData.append('amount', amount.toString());
      uploadData.append('useAi', useAi.toString());
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/expenses/${timestampedName}/upload`, uploadData)
        .toPromise()
        .then((res) => {
          this.clearProperties();
          return res;
        });
  }

  deleteExpense(propertyId, expense){
      return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/expenses/${expense.id}`).toPromise().then((res) => {
        this.clearProperties();
        return res;
      });
  }

  refreshExpense(propertyId, expense){
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/expenses/${expense.id}/refresh`, {}).toPromise().then((res) => {
        this.clearProperties();
        return res;
      });
  }

  refreshExpenses(propertyId){
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/expenses/refresh`, {}).toPromise().then((res) => {
        this.clearProperties();
        return res;
      });
  }

  getExpenseById(expenseId: string) {
    return this.client.get(`${environment.apiBaseUrl}/v1/admin/expenses/${expenseId}`).toPromise();
  }

  updateExpense(expense: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/expenses/${expense.id}`, expense, httpOptions).toPromise().then((res) => {
      this.clearProperties();
      return res;
    });
  }

  /** Upload a statement. Rejects with 409 DUPLICATE_FILE unless override is true. */
  /** Create a statement with no file (manual entry); review/apply as usual. */
  createManualStatement(propertyId: string) {
    return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/manual`, {})
      .toPromise()
      .then((res) => {
        this.clearProperties();
        return res;
      });
  }

  uploadStatement(propertyId, file, override: boolean = false){
      const timestampedName = Date.now() + '-' + file.name;
      const form = new FormData();
      form.append('file', file, timestampedName);
      const query = override ? '?override=true' : '';
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${timestampedName}/upload${query}`, form)
        .toPromise()
        .then((res) => {
          this.clearProperties();
          this.clearInvoices({id: propertyId});
          return res;
        });
  }

  deleteStatement(propertyId, statement){
      return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}`).toPromise().then((res) => {
          this.clearProperties();
          this.clearInvoices({id: propertyId});
          return res;
      });
  }

  analyzeStatement(propertyId, statement){
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/analyze`, {}).toPromise();
  }

  /** Apply the reviewed analysis. body: { analysis?, override? }. Never re-runs the AI. */
  applyStatement(propertyId, statement, body: any = {}){
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/apply`, body).toPromise().then((res) => {
          this.clearProperties();
          this.clearInvoices({id: propertyId});
          return res;
      });
  }

  hasStatementBeenApplied(propertyId, statement){
      return this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/applied`).toPromise();
  }

  getStatementAppliedItems(propertyId, statement){
      return this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/applied-items`).toPromise();
  }

  /** Save the reviewed analysis. Rejects with 409 DUPLICATE_PERIOD unless override is true. */
  saveAnalysis(propertyId, statement, analysis, override: boolean = false){
      return this.client.post(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/analysis/save`, { analysis, override }).toPromise();
  }

  getSavedAnalysis(propertyId, statement){
      return this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/analysis`).toPromise();
  }

  deleteAnalysis(propertyId, statement){
      return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/analysis`).toPromise();
  }

  removeAnalysis(propertyId, statement){
      return this.client.delete(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${statement.id}/analysis/remove`).toPromise().then((res) => {
          this.clearProperties();
          this.clearInvoices({id: propertyId});
          return res;
      });
  }

  getStatementFile(propertyId, fileName){
      return this.client.get(`${environment.apiBaseUrl}/v1/admin/properties/${propertyId}/statements/${fileName}`, { responseType: 'blob' }).toPromise();
  }

  getAllStatements(page?: number, pageSize?: number, propertyId?: string) {
    let params = new HttpParams();
    if (page !== undefined && page !== null) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (propertyId) {
      params = params.set('propertyId', propertyId);
    }
    return this.client.get(`${environment.apiBaseUrl}/v1/admin/statements`, { params }).toPromise();
  }
}
